/* Uit Krabbelpaal.dc.html; bodies letterlijk overgenomen. */

import { api, asJson } from '../lib/api.js';
import { parseLikedBy } from '../lib/pixels.js';
import { dayKey, parseTs } from '../lib/text.js';
import { playParty, playPop } from '../lib/sound.js';
import * as store from '../lib/store.js';

export default {
  /**
   * Welke krabbels meedoen voor de krabbel van de dag, en op welke dag we zitten.
   *
   * Alleen krabbels van vóór vandaag: de krabbel van de dag hoort er al te hangen
   * als je 's ochtends binnenkomt, en niet halverwege de dag te verspringen omdat
   * er iemand iets nieuws plakte. Hangt er nog niets van gisteren, dan doet alles
   * mee, want anders is er op dag één geen krabbel van de dag.
   *
   * Op id gesorteerd en niet op tijd: een id verandert nooit meer, dus iedereen
   * krijgt dezelfde rij te zien.
   */
  dagPot(vanaf) {
      // Zonder datum: vandaag. Het modvenster vraagt om morgen, en dan doet alles
      // van vandaag ook mee, net als morgen echt gebeurt.
      const dagStart = vanaf ? new Date(vanaf) : new Date();
      dagStart.setHours(0, 0, 0, 0);
      const pot = this.state.galleryItems.filter((g) => {
        const t = parseTs(g.created);
        return !isNaN(t) && t < dagStart.getTime();
      });
      const bron = pot.length ? pot : this.state.galleryItems;
      return { dagStart, rij: bron.slice().sort((a, b) => (a.id < b.id ? -1 : 1)) };
    },

  /**
   * Wie vandaag de krabbel van de dag is, en wie het geworden zou zijn.
   *
   * Heeft de mod er een aangewezen voor vandaag, dan is die het. Zo niet, dan wijst
   * het dagnummer er een aan uit de rij hierboven: geen server nodig, en toch
   * dezelfde krabbel voor iedereen.
   *
   * Deze som stond als losse variabele in renderVals, en daarmee bestond `openDaily`
   * alleen als knopje in die vals. Het pictogram op het bureaublad hangt aan
   * `this.openDaily`, en dat was dus niets: erop klikken deed helemaal niks.
   */
  dagKeuze() {
      const { dagStart, rij } = this.dagPot();
      const rec = this.state.dailyRec && this.state.dailyRec.day === dayKey() ? this.state.dailyRec : null;
      const gekozen = rec && rec.drawingId ? this.state.galleryItems.find((g) => g.id === rec.drawingId) : null;
      const zelf = rij.length ? rij[Math.floor(dagStart.getTime() / 86400000) % rij.length] : null;
      return { gekozen: gekozen || null, zelf };
    },

  /**
   * En dan de krabbel van de dag zelf: de aangewezene, of anders wat de dag koos.
   *
   * Het modvenster wil die twee apart weten ("vandaag hangt X er al" tegenover
   * "vandaag koos de paal zelf X"), en daarom geeft dagKeuze ze los terug.
   */
  krabbelVanDeDag() {
      const k = this.dagKeuze();
      return k.gekozen || k.zelf;
    },

  /** Hem openen: een feestje, wat confetti, en de krabbel groot in beeld. */
  openDaily() {
      const g = this.krabbelVanDeDag();
      if (!g) return;
      playParty();
      this.fireConfetti();
      this.openViewer(g.id);
    },

  startGalleryPoll() {
      this.stopGalleryPoll();
      this.galleryTick = 0;
      this.galleryTimer = setInterval(() => {
        if (document.hidden) return;
        // Vangnet zodra de lijn openstaat: een nieuwe krabbel meldt zichzelf al.
        if (this.liveSkip(this.galleryTick++, 4)) return;
        this.refreshGallery(true);
      }, 15000);
    },

  stopGalleryPoll() { if (this.galleryTimer) { clearInterval(this.galleryTimer); this.galleryTimer = null; } },

  notify(text, avatar) {
      clearTimeout(this.toastTimer);
      this.setState({ toast: text, toastAvatar: avatar || '' });
      this.toastTimer = setTimeout(() => this.setState({ toast: '', toastAvatar: '' }), 5500);
    },

  fireConfetti() {
      const colors = ['var(--roze)', 'var(--paars)', 'var(--groen)', '#FFA300', '#29ADFF'];
      const pieces = Array.from({ length: 34 }, (_, i) => ({
        id: 'c' + Date.now() + '_' + i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        dur: 1.4 + Math.random() * 0.9,
        color: colors[i % colors.length],
        w: 7 + Math.round(Math.random() * 6),
        h: 11 + Math.round(Math.random() * 8)
      }));
      clearTimeout(this.confettiTimer);
      this.setState({ confetti: pieces });
      this.confettiTimer = setTimeout(() => this.setState({ confetti: [] }), 2900);
    },

  openViewer(id) {
      // Een venster dat opengaat hoort bovenop te liggen. Zonder deze regel kwam de
      // krabbel achter het tekenvenster te liggen, want de viewer stond onderaan in
      // de stapelorde en was daar nooit uit gekomen.
      this.vensterNaarVoren('viewer');
      // En hem weer in beeld zetten als hij weggeklapt was: anders veranderde de
      // krabbel erin maar bleef het venster onzichtbaar.
      this.setState({ viewerId: id, likersOpen: false, likersAt: null, commentText: '', commentError: '',
        vensterMin: Object.assign({}, this.state.vensterMin, { viewer: false }) });
      this.fetchComments();
    },

  likersAnchor(e) {
      const r = e && e.currentTarget && e.currentTarget.getBoundingClientRect();
      return r ? { x: r.left + r.width / 2, y: r.top, b: r.bottom, l: r.left, r: r.right } : null;
    },

  showLikers(e) { return this.setState({ likersOpen: true, likersAt: this.likersAnchor(e) }); },

  hideLikers() { return this.setState({ likersOpen: false }); },

  toggleLikers(e) {
      const at = this.likersAnchor(e);
      this.setState((st) => ({ likersOpen: !st.likersOpen, likersAt: st.likersOpen ? null : at }));
    },

  closeViewer() { return this.setState({ viewerId: null, likersOpen: false, likersAt: null, vensterMin: Object.assign({}, this.state.vensterMin, { viewer: false }) }); },

  // ---- Adressen ----
  // Elk scherm heeft zijn eigen #hash, zodat je een link kunt sturen die bij de
  // ander hetzelfde scherm opent. De hash volgt de staat; een nieuwe hash opent
  // het scherm.
  routeOf(s) {
      if (s.viewerId) return 'krabbel/' + s.viewerId;
      if (s.dialog === 'account' && s.profileId) return 'profiel/' + s.profileId;
      // Wat hier niet meer bij staat: de Krabbelgotchi, de Krabbellijn, het leaderbord,
      // het gastenboek en de arcadehal. Dat waren allemaal standen van `state.dialog`;
      // het zijn nu vensters met hun eigen vlag, die naast elkaar open kunnen staan.
      // Dan is er geen één adres meer dat de toestand beschrijft, precies zoals bij
      // `tekenen`: je kunt er wél naartoe met een link, maar de app schrijft dat adres
      // niet zelf meer in de balk.
      const named = { account: 'account', inbox: 'postvakje', feature: 'ideeenbus', mod: 'mod', snake: 'krabbelworm', memory: 'krabbelry' }[s.dialog];
      if (named) return named;
      /**
       * De paal is het adres van het bureaublad, ook als er getekend wordt.
       *
       * Deze functie geeft precies één adres terug, en met een tekenvenster dat over
       * de paal heen ligt is dat geen of-of meer. `tekenen` blijft dus een adres waar
       * je binnen kunt komen, maar niet een adres dat de app zelf schrijft. Datzelfde
       * doen `krabbelmemory` en `krabbelbeestje` hieronder al.
       */
      return 'paal';
    },

  applyRoute(raw) {
      const parts = String(raw || '').replace(/^#\/?/, '').split('/');
      const key = (parts[0] || 'paal').toLowerCase();
      const arg = parts[1] ? decodeURIComponent(parts[1]) : '';
      this.routeBusy = true;
      const done = () => { this.routeBusy = false; };
      // welcome hoort hier ook bij. Het welkomstscherm komt bij elke start op, en
      // wie via een gedeelde link binnenkomt wil de krabbel uit die link zien en
      // niet eerst een begroeting die daar bovenop ligt.
      const clear = { viewerId: null, profileId: null, dialog: null, notifOpen: false, chatOpen: false, startOpen: false, welcome: false };
      if (key === 'krabbel' && arg) {
        // Ook hier niet wegklappen op een computer: de krabbelviewer is een venster en
        // komt gewoon naast of over het tekenvenster te liggen.
        this.setState(Object.assign({}, clear, this.state.isMobile ? { studioMin: true } : {}), () => { this.openViewer(arg); this.startGalleryPoll(); done(); });
        return;
      }
      if (key === 'profiel' && arg) { this.setState(clear, () => { this.openProfile(arg); done(); }); return; }
      if (key === 'tekenen') {
        // Op een telefoon kun je niet tekenen, dus daar landt deze link op de paal.
        // Stil doorsturen zou raadselachtig zijn: dan klik je een link en kom je
        // ergens anders uit zonder te weten waarom.
        if (this.state.isMobile) {
          this.setState(Object.assign({}, clear, { studioMin: true }), () => {
            this.notify('Tekenen gaat op een computer; op je telefoon kun je kijken en kletsen.');
            this.startGalleryPoll();
            done();
          });
          return;
        }
        this.setState(clear, () => { this.goStudio(); done(); });
        return;
      }
      const open = {
        account: () => this.openAccount(),
        postvakje: this.openInbox,
        ideeenbus: () => this.setState({ dialog: 'feature', featureFilter: 'open', featureText: '', featureError: '', featureNote: '', featureSent: false }, this.fetchFeatures),
        leaderbord: this.openActivity,
        mod: this.openMod,
        gastenboek: this.openGuestbook,
        krabbelworm: this.openSnake,
        krabbelry: this.openMemory,
        // Het spel heette Memory; dit oude adres blijft werken.
        krabbelmemory: this.openMemory,
        krabbelgotchi: this.openTamagotchi,
        // Het spel heette Krabbelbeestje. Dit oude adres blijft werken, zodat een
        // link die al ergens staat niet op de paal uitkomt. syncHash schrijft
        // voortaan de nieuwe naam in de balk.
        krabbelbeestje: this.openTamagotchi,
        krabbellijn: this.openKetting,
        arcadehal: this.openArcade
      }[key];
      /**
       * Een adres volgen klapt het tekenvenster niet weg.
       *
       * Op een computer hoeft er nooit iets voor te wijken. De paal is het bureaublad
       * en ligt altijd al in beeld, en wat er opengaat is een venster of een
       * schermvullende laag die eroverheen komt. Zonder deze regel klapte elke
       * herlading het tekenvenster weg, want `routeOf` schrijft `#paal` in de
       * adresbalk zodra je binnen bent en bij het opstarten loopt `applyRoute` over
       * die hash: eerste bezoek venster open, F5 venster weg.
       *
       * En het gold niet alleen voor de paal. Klikte je het leaderbord of de
       * ideeënbus open, dan klapte je doek weg en moest je het daarna zelf uit de
       * taakbalk terughalen — terwijl die vensters er gewoon overheen liggen.
       *
       * Verversen hoeft hier niet: de klok van vijftien seconden loopt al zolang de
       * paal in beeld is, en die is dat op een computer altijd. Zie paalInBeeld in
       * logic/navigation.js.
       *
       * Op de telefoon blijft het een of-of, dus daar klapt hij wel weg.
       */
      const wegklappen = this.state.isMobile;
      this.setState(Object.assign({}, clear, wegklappen ? { studioMin: true } : {}), () => {
        if (open) open(); else if (wegklappen) this.goGallery();
        done();
      });
    },

  syncHash() {
      if (this.routeBusy) return;
      const want = '#' + this.routeOf(this.state);
      if (window.location.hash === want) return;
      try { history.replaceState(null, '', want); } catch (e) { window.location.hash = want; }
    },

  onHashChange() {
      if (window.location.hash === '#' + this.routeOf(this.state)) return;
      this.applyRoute(window.location.hash);
    },

  tipEnter(id) { return (e) => {
      if (this.state.isMobile) return;
      const el = e.currentTarget;
      clearTimeout(this.hoverTipTimer);
      this.hoverTipTimer = setTimeout(() => {
        if (!el.isConnected) return;
        const r = el.getBoundingClientRect();
        this.setState({ hoverTip: { id: id, at: { x: Math.round(r.right + 15), y: Math.round(r.top + r.height / 2) } } });
      }, 333);
    }; },

  tipLeave() {
      clearTimeout(this.hoverTipTimer);
      if (this.state.hoverTip) this.setState({ hoverTip: null });
    },

  /*
   * Hier stonden de coachtips: paarse ballonnen met een NIEUW-vlaggetje die uit
   * zichzelf naast een gereedschap opdoken tot je ze wegklikte. Weg, samen met de
   * andere vensters die zichzelf opendeden. De tips zelf zijn er nog: wijs een
   * gereedschap aan en na een derde seconde staat dezelfde uitleg er (tipEnter
   * hierboven). Die vraag je zelf op, en dat is het verschil.
   */

  async refreshGallery(silent) {
      if (!this.state.connected) return;
      if (!silent) this.setState({ galleryLoading: true, galleryError: '' });
      try {
        const res = await api(`${this.state.pbUrl}/api/collections/${this.collectionName}/records?sort=-created&perPage=300`);
        if (!res.ok) throw new Error(`Server antwoordde ${res.status}.`);
        const data = await res.json();
        const items = (data.items || []).filter((r) => r.image).map((r, i) => {
          const rot = ((r.id.charCodeAt(0) + r.id.charCodeAt(1) * 3) % 9) - 4;
          return {
            id: r.id,
            title: r.title || 'Naamloos',
            author: r.author || 'onbekend',
            accountId: r.accountId || '',
            coAuthorId: r.coAuthorId || '',
            isThemeMatch: !!r.isThemeMatch,
            themeText: r.themeText || '',
            frames: Number(r.frames) || 1,
            src: `${this.state.pbUrl}/api/files/${this.collectionName}/${r.id}/${r.image}`,
            rot,
            index: i,
            likesCount: typeof r.likes === 'number' ? r.likes : 0,
            likedBy: parseLikedBy(r.likedBy),
            created: r.created || ''
          };
        });
        const sig = (list) => list.map((i) => i.id + ':' + i.title + ':' + i.likesCount + ':' + i.isThemeMatch + ':' + i.themeText + ':' + i.likedBy.map((l) => l.id || l.n).join(',')).join('|');
        const mine = items.reduce((sum, g) => this.isMine(g) ? sum + g.likesCount : sum, 0);
        if (this.likesInit) {
          this.likesInit = false;
          store.set('gp_likes_seen', String(mine));
          this.setState({ likesSeen: mine });
        } else if (mine > this.state.likesSeen && this.state.author && this.likePinged !== mine) {
          // Alleen piepen als er écht een like bijkomt, niet elke poll opnieuw.
          this.likePinged = mine;
          playPop();
        }
        if (silent && sig(items) === sig(this.state.galleryItems)) return;
        this.setState({ galleryItems: items, galleryLoading: false, galleryError: '' });
      } catch (err) {
        if (!silent) { this.setState({ galleryLoading: false, galleryError: err.message }); this.notify('De paal kon niet geladen worden: ' + err.message); }
      }
    },

  didILike(g) {
      if (!g) return false;
      const mine = g.likedBy && g.likedBy.some((l) => (l.id ? l.id === this.state.accountId : l.n === this.state.author));
      return mine || this.state.likedIds.includes(g.id);
    },

  async toggleLike(id) {
      if (!this.ensureName('Log in of maak een account aan om hartjes te geven.')) return;
      const target = this.state.galleryItems.find((it) => it.id === id);
      const liked = this.didILike(target);
      if (!liked) playPop();
      const item = this.state.galleryItems.find((it) => it.id === id);
      if (!item) return;
      const delta = liked ? -1 : 1;
      const optimistic = Math.max(0, item.likesCount + delta);
      const mineOf = (l) => (l.id ? l.id === this.state.accountId : l.n === this.state.author);
      const optimisticLikedBy = liked
        ? item.likedBy.filter((l) => !mineOf(l))
        : [...item.likedBy.filter((l) => !mineOf(l)), { id: this.state.accountId || '', n: this.state.author || 'iemand', t: Date.now() }];
      const likedIds = liked ? this.state.likedIds.filter((x) => x !== id) : [...this.state.likedIds, id];
      if (this.isMine(item)) {
        const seen = Math.max(0, this.state.likesSeen + delta);
        store.set('gp_likes_seen', String(seen));
        this.setState({ likesSeen: seen });
      }
      store.set('gp_liked_ids', JSON.stringify(likedIds));
      this.setState({
        likedIds,
        galleryItems: this.state.galleryItems.map((it) => it.id === id ? { ...it, likesCount: optimistic, likedBy: optimisticLikedBy } : it)
      });
      try {
        const rres = await api(`${this.state.pbUrl}/api/collections/${this.collectionName}/records/${id}`);
        const rec = rres.ok ? await rres.json() : null;
        const base = rec && typeof rec.likes === 'number' ? rec.likes : item.likesCount;
        const finalLikes = Math.max(0, base + delta);
        const serverLikedBy = parseLikedBy(rec && rec.likedBy).filter((l) => !mineOf(l));
        const finalLikedBy = liked ? serverLikedBy : [...serverLikedBy, { id: this.state.accountId || '', n: this.state.author || 'iemand', t: Date.now() }];
        const pres = await api(`${this.state.pbUrl}/api/collections/${this.collectionName}/records/${id}`, asJson('PATCH', { likes: finalLikes, likedBy: JSON.stringify(finalLikedBy) }));
        if (pres.ok) {
          const rj = await pres.json().catch(() => null);
          if (rj && !('likedBy' in rj) && !this.likedByWarned) {
            this.likedByWarned = true;
            this.notify('Namen bij likes worden nog niet opgeslagen: voeg in PocketBase een tekstveld "likedBy" toe aan paal_drawings.');
          }
          const stored = rj && 'likedBy' in rj ? parseLikedBy(rj.likedBy) : finalLikedBy;
          this.setState({ galleryItems: this.state.galleryItems.map((it) => it.id === id ? { ...it, likesCount: finalLikes, likedBy: stored } : it) });
        }
      } catch (e) {}
    },

  duoPartnerId() {
      const d = this.state.duo;
      if (!d) return '';
      return d.aId === this.state.accountId ? d.bId : d.aId;
    }
};
